import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { usageApi, type UsageRange } from "@/api/usage";
import {
  Users,
  MessageSquare,
  Activity as ActivityIcon,
  Zap,
  DollarSign,
  LogIn,
  Bot,
} from "lucide-react";

/**
 * Per-tenant usage metrics dashboard.
 *
 * Five sections:
 *   1. Summary cards (active users, sessions, messages, tokens, cost)
 *   2. Daily activity sparkline (messages + token totals per day)
 *   3. Per-user breakdown
 *   4. Per-agent breakdown (with model mix)
 *   5. Recent sign-ins (instance-admin only)
 *
 * All queries flow through /api/usage/* which is gated by
 * requireCompanyAccess. Token data comes out of chat_messages.metadata
 * jsonb, so this page degrades gracefully on historic rows where usage
 * wasn't captured — they simply contribute 0.
 */
export function TenantUsage() {
  const { tenantId, isInstanceAdmin } = useTenant();

  const [days, setDays] = useState<7 | 30 | 90>(30);
  const range: UsageRange = useMemo(
    () => ({
      from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
      to: new Date().toISOString(),
    }),
    [days],
  );

  const { data: summary } = useQuery({
    queryKey: ["usage", "summary", tenantId, days],
    queryFn: () => usageApi.summary(tenantId, range),
  });

  const { data: timeseries = [] } = useQuery({
    queryKey: ["usage", "timeseries", tenantId, days],
    queryFn: () => usageApi.timeseries(tenantId, range),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["usage", "users", tenantId, days],
    queryFn: () => usageApi.users(tenantId, range),
  });

  const { data: agents = [], isLoading: agentsLoading } = useQuery({
    queryKey: ["usage", "agents", tenantId, days],
    queryFn: () => usageApi.agents(tenantId, range),
  });

  const { data: signins = [] } = useQuery({
    queryKey: ["usage", "signins", tenantId, days],
    queryFn: () => usageApi.signins(tenantId, range),
    enabled: isInstanceAdmin,
  });

  return (
    <div className="space-y-5">
      {/* Header + range picker */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Usage Metrics</h2>
          <p className="text-sm text-text-secondary">
            Activity and token usage for this tenant.
          </p>
        </div>
        <div className="flex gap-1">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={
                "rounded-md border px-3 py-1 text-xs transition-colors " +
                (days === d
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-border text-text-secondary hover:bg-surface-hover")
              }
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard
          icon={Users}
          label="Active users"
          value={summary?.activeUsers ?? 0}
        />
        <SummaryCard
          icon={MessageSquare}
          label="Sessions"
          value={summary?.totalSessions ?? 0}
        />
        <SummaryCard
          icon={ActivityIcon}
          label="Messages"
          value={summary?.totalMessages ?? 0}
          sublabel={
            summary
              ? `${summary.userMessages.toLocaleString()} user · ${summary.assistantMessages.toLocaleString()} agent`
              : undefined
          }
        />
        <SummaryCard
          icon={Zap}
          label="Input tokens"
          value={summary?.inputTokens ?? 0}
          format="compact"
          sublabel={
            summary && summary.cacheReadTokens > 0
              ? `${formatCompact(summary.cacheReadTokens)} cache hit`
              : undefined
          }
        />
        <SummaryCard
          icon={Zap}
          label="Output tokens"
          value={summary?.outputTokens ?? 0}
          format="compact"
        />
        <SummaryCard
          icon={DollarSign}
          label="Cost"
          value={(summary?.totalCostCents ?? 0) / 100}
          format="dollars"
        />
      </div>

      {/* Daily activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily activity</CardTitle>
        </CardHeader>
        <CardContent>
          {timeseries.length === 0 ? (
            <p className="text-sm text-text-muted">No activity in this window.</p>
          ) : (
            <DailySparkline points={timeseries} />
          )}
        </CardContent>
      </Card>

      {/* Per-user breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Users</CardTitle>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="py-2 pr-3 font-medium">User</th>
                  <th className="py-2 pr-3 font-medium">Role</th>
                  <th className="py-2 pr-3 font-medium">Last login</th>
                  <th className="py-2 pr-3 text-right font-medium">Sessions</th>
                  <th className="py-2 pr-3 text-right font-medium">Messages</th>
                  <th className="py-2 pr-3 text-right font-medium">In</th>
                  <th className="py-2 text-right font-medium">Out</th>
                </tr>
              </thead>
              <tbody>
                {[...users]
                  .sort((a, b) => b.messages - a.messages)
                  .map((u) => (
                    <tr key={u.tenantUserId} className="border-b border-border/40">
                      <td className="py-2 pr-3">
                        <div className="text-text-primary">
                          {u.displayName ?? u.email}
                        </div>
                        {u.displayName && (
                          <div className="text-xs text-text-muted">{u.email}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="secondary" className="text-[10px]">
                          {u.role}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-text-muted">
                        {u.lastLoginAt
                          ? new Date(u.lastLoginAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {u.sessions}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {u.messages}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-text-secondary">
                        {formatCompact(u.inputTokens)}
                      </td>
                      <td className="py-2 text-right font-mono text-text-secondary">
                        {formatCompact(u.outputTokens)}
                      </td>
                    </tr>
                  ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-text-muted">
                      No users in this tenant yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Per-agent breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Agents</CardTitle>
        </CardHeader>
        <CardContent>
          {agentsLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <Bot size={28} className="mb-2 text-text-muted" />
              <p className="text-sm text-text-muted">No agents yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="py-2 pr-3 font-medium">Agent</th>
                  <th className="py-2 pr-3 text-right font-medium">Sessions</th>
                  <th className="py-2 pr-3 text-right font-medium">Messages</th>
                  <th className="py-2 pr-3 text-right font-medium">In</th>
                  <th className="py-2 pr-3 text-right font-medium">Out</th>
                  <th className="py-2 pr-3 text-right font-medium">Cache hit</th>
                  <th className="py-2 pr-3 font-medium">Models</th>
                  <th className="py-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {[...agents]
                  .sort(
                    (a, b) =>
                      b.inputTokens + b.outputTokens -
                      (a.inputTokens + a.outputTokens),
                  )
                  .map((a) => (
                    <tr key={a.agentId} className="border-b border-border/40">
                      <td className="py-2 pr-3 text-text-primary">{a.agentName}</td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {a.sessions}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {a.messages}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-text-secondary">
                        {formatCompact(a.inputTokens)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-text-secondary">
                        {formatCompact(a.outputTokens)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-text-secondary">
                        {formatCompact(a.cacheReadTokens)}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(a.modelBreakdown).map(([model, count]) => (
                            <Badge
                              key={model}
                              variant="secondary"
                              className="text-[10px]"
                            >
                              {shortModel(model)} · {count}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 text-right font-mono text-text-secondary">
                        ${(a.costCents / 100).toFixed(2)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Recent sign-ins (instance-admin only) */}
      {isInstanceAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <LogIn size={16} />
              Recent sign-ins
              <span className="text-xs font-normal text-text-muted">
                (instance admin)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signins.length === 0 ? (
              <p className="text-sm text-text-muted">No sign-ins in this window.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-text-muted">
                    <th className="py-2 pr-3 font-medium">User</th>
                    <th className="py-2 pr-3 font-medium">Provider</th>
                    <th className="py-2 pr-3 font-medium">IP</th>
                    <th className="py-2 pr-3 font-medium">User-agent</th>
                    <th className="py-2 text-right font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {signins.map((s) => (
                    <tr key={s.authSessionId} className="border-b border-border/40">
                      <td className="py-2 pr-3 text-text-primary">
                        {s.displayName ?? s.email}
                      </td>
                      <td className="py-2 pr-3 text-text-secondary">
                        {s.authProvider}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-text-secondary">
                        {s.ipAddress ?? "—"}
                      </td>
                      <td className="py-2 pr-3 text-xs text-text-muted">
                        {truncate(s.userAgent, 60)}
                      </td>
                      <td className="py-2 text-right text-xs text-text-muted">
                        {new Date(s.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------- helpers ----------

function SummaryCard({
  icon: Icon,
  label,
  value,
  sublabel,
  format,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: number;
  sublabel?: string;
  format?: "compact" | "dollars";
}) {
  const display =
    format === "dollars"
      ? `$${value.toFixed(2)}`
      : format === "compact"
        ? formatCompact(value)
        : value.toLocaleString();
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <Icon size={12} />
          {label}
        </div>
        <div className="text-2xl font-semibold text-text-primary">{display}</div>
        {sublabel && (
          <div className="text-[11px] text-text-muted">{sublabel}</div>
        )}
      </CardContent>
    </Card>
  );
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function shortModel(m: string): string {
  // Strip vendor prefix and date suffix for a compact badge.
  // claude-sonnet-4-6 → sonnet-4-6 ; claude-haiku-4-5 → haiku-4-5
  return m.replace(/^(claude-|anthropic\/)/, "");
}

function truncate(s: string | null, n: number): string {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

/**
 * Lightweight inline sparkline-table. Avoids a chart dependency: just
 * renders proportional bars next to each day. Keeps the page snappy and
 * matches the existing low-chrome aesthetic.
 */
function DailySparkline({
  points,
}: {
  points: { day: string; messages: number; inputTokens: number; outputTokens: number }[];
}) {
  const maxMessages = Math.max(1, ...points.map((p) => p.messages));
  const maxTokens = Math.max(
    1,
    ...points.map((p) => p.inputTokens + p.outputTokens),
  );
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[80px_1fr_72px_1fr_72px] gap-2 border-b border-border pb-1 text-xs text-text-muted">
        <span>Day</span>
        <span>Messages</span>
        <span className="text-right">Count</span>
        <span>Tokens (in+out)</span>
        <span className="text-right">Total</span>
      </div>
      {points.map((p) => (
        <div
          key={p.day}
          className="grid grid-cols-[80px_1fr_72px_1fr_72px] items-center gap-2 text-xs"
        >
          <span className="font-mono text-text-secondary">{p.day}</span>
          <div className="h-2 rounded bg-surface-hover">
            <div
              className="h-full rounded bg-accent/60"
              style={{ width: `${(p.messages / maxMessages) * 100}%` }}
            />
          </div>
          <span className="text-right font-mono text-text-secondary">
            {p.messages}
          </span>
          <div className="h-2 rounded bg-surface-hover">
            <div
              className="h-full rounded bg-accent/30"
              style={{
                width: `${((p.inputTokens + p.outputTokens) / maxTokens) * 100}%`,
              }}
            />
          </div>
          <span className="text-right font-mono text-text-secondary">
            {formatCompact(p.inputTokens + p.outputTokens)}
          </span>
        </div>
      ))}
    </div>
  );
}
